import { createRouter, createWebHistory } from 'vue-router'

import { useAuthStore } from '@/stores/auth'
import { useAccountStore } from '@/stores/account'
import { loadLayout } from '@/router/middleware/layout'

const routes = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { isPublic: true, layout: 'LoginLayout', unauthenticatedOnly: true },
  },
  {
    path: '/login/:providerId',
    name: 'loginOAuth',
    component: () => import('@/views/auth/LoginOAuthView.vue'),
    props: true,
    meta: { isPublic: true, layout: 'LoginLayout', unauthenticatedOnly: true },
  },
  {
    path: '/reset-password',
    name: 'resetPassword',
    component: () => import('@/views/auth/ResetPasswordView.vue'),
    meta: { isPublic: true, layout: 'LoginLayout', unauthenticatedOnly: true },
  },
  {
    path: '/newpassword',
    redirect: '/new-password',
  },
  {
    path: '/new-password',
    name: 'newPassword',
    component: () => import('@/views/auth/NewPasswordView.vue'),
    meta: { isPublic: true, layout: 'LoginLayout' },
  },
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/DashboardView.vue'),
    meta: { isPublic: false },
  },
  {
    path: '/dogs',
    name: 'dogs',
    component: () => import('@/views/dogs/DogsView.vue'),
    meta: { isPublic: false },
    beforeEnter: (to, from, next) => {
      const accountStore = useAccountStore()

      if (accountStore.isSubscriptionPausedOrFailedPayment) {
        next({ name: 'home' })
      } else if (accountStore.hasMultipleDogs) {
        next()
      } else {
        next({ name: 'dogDetails', params: { dogId: accountStore.dogs[0]._id } })
      }
    },
  },
  {
    path: '/dogs/:dogId',
    name: 'dogDetails',
    component: () => import('@/views/dogs/DogDetailsView.vue'),
    meta: { isPublic: false },
    beforeEnter: (to, from, next) => {
      const accountStore = useAccountStore()

      if (accountStore.isSubscriptionPausedOrFailedPayment) {
        next({ name: 'home' })
      } else {
        next()
      }
    },
  },
  {
    path: '/dogs/add',
    name: 'dogAdditionForm',
    component: () => import('@/views/dogs/DogAdditionForm.vue'),
    meta: { layout: 'DogAdditionLayout', isPublic: false },
    beforeEnter: async (to, from, next) => {
      // Avoid entering to this page if the customer can't add a dog.
      const accountStore = useAccountStore()

      if (accountStore.isSubscriptionPausedOrFailedPayment) {
        next({ name: 'home' })
      } else if (accountStore.canAddDog) {
        next()
      } else {
        next({ name: 'dogs' })
      }
    },
  },
  {
    path: '/dogs/add/checkout',
    name: 'dogAdditionCheckout',
    component: () => import('@/views/dogs/DogAdditionCheckout.vue'),
    meta: { layout: 'DogAdditionLayout', isPublic: false },
    beforeEnter: async (to, from, next) => {
      // Avoid entering to this page if there's not a dog created as the quotation could not be generated.
      const accountStore = useAccountStore()

      if (accountStore.isSubscriptionPausedOrFailedPayment) {
        next({ name: 'home' })
      } else {
        next()
      }
    },
  },
  {
    path: '/orders',
    meta: { layout: 'OrderLayout', isPublic: false },
    children: [
      {
        path: '',
        name: 'orders',
        component: () => import('@/views/orders/OrdersView.vue'),
      },
      {
        path: ':orderId',
        name: 'orderDetail',
        props: true,
        component: () => import('@/views/orders/OrderDetailView.vue'),
      },
    ],
  },
  // {
  //   path: '/store/snacks',
  //   name: 'snacks',
  //   component: () => import('@/views/store/snacks/SnacksView.vue'),
  //   meta: { isPublic: false },
  // },
  // {
  //   path: '/advantages/nutritionists',
  //   name: 'nutritionists',
  //   component: () => import('@/views/advantages/AdvantagesNutritionistsView.vue'),
  //   meta: { isPublic: false },
  // },
  // {
  //   path: '/advantages/guides',
  //   name: 'guides',
  //   component: () => import('@/views/advantages/AdvantagesGuidesView.vue'),
  //   meta: { isPublic: false },
  // },
  // {
  //   path: '/referrals',
  //   name: 'referrals',
  //   component: () => import('@/views/ReferralsView.vue'),
  //   meta: { isPublic: false },
  // },
  {
    path: '/account',
    // Name 'account' needed for breadcrumb structure in the mobile view.
    name: 'account',
    meta: { layout: 'AccountLayout', isPublic: false },
    children: [
      {
        path: 'user',
        name: 'accountUser',
        component: () => import('@/views/account/AccountUserView.vue'),
      },
      {
        path: 'password',
        name: 'accountPassword',
        component: () => import('@/views/account/AccountPasswordView.vue'),
      },
      {
        path: 'address',
        name: 'accountAddress',
        component: () => import('@/views/account/AccountAddressView.vue'),
      },
      {
        path: 'payment',
        name: 'accountPayment',
        component: () => import('@/views/account/AccountPaymentView.vue'),
      },
      {
        path: 'subscription',
        children: [
          {
            path: '',
            name: 'subscription',
            component: () => import('@/views/account/subscription/SubscriptionView.vue'),
            beforeEnter: (to, from, next) => {
              const accountStore = useAccountStore()

              to.meta.isHiddenMobile = !accountStore.isSubscriptionPausedOrFailedPayment

              next()
            },
          },
          {
            path: 'plan',
            name: 'subscriptionPlan',
            component: () => import('@/views/account/subscription/SubscriptionPlanView.vue'),
            meta: { isHiddenMobile: false },
          },
          {
            path: 'orders',
            name: 'subscriptionOrders',
            component: () => import('@/views/account/subscription/SubscriptionOrdersView.vue'),
          },
          {
            path: 'extras',
            name: 'subscriptionExtras',
            component: () => import('@/views/account/subscription/SubscriptionExtrasView.vue'),
          },
        ],
        beforeEnter: (to, from, next) => {
          const { isSubscriptionPausedOrFailedPayment } = useAccountStore()

          // If user does not have an active subscription and tries to navigate to a restricted route, redirect to subscription view.
          next((isSubscriptionPausedOrFailedPayment) && ['subscriptionPlan', 'subscriptionOrders', 'subscriptionExtras'].includes(to.name)
            ? { name: 'subscription' }
            : undefined,
          )
        },
      },
    ],
  },
  {
    path: '/transition',
    name: 'transition',
    component: () => import('@/views/TransitionView.vue'),
    meta: { isPublic: false },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'notFound',
    // We redirect to home page if the route is not found.
    redirect: { name: 'home' },
    meta: { isPublic: false, avoidRedirect: true },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior (to, from, savedPosition) {
    if (to.hash) {
      // Wait a few milliseconds to allow the components be mounted.
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          resolve({
            el: to.hash,
            behavior: 'smooth',
          })
        }, 250)
      })
    }

    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0, behavior: 'smooth' }
    }
  },
})

router.beforeEach(async (to, from, next) => {
  const isPublicRoute = to.meta.isPublic
  const unauthenticatedOnly = to.meta.unauthenticatedOnly

  const authStore = useAuthStore()

  if (!authStore.isReady) await authStore.init()

  const isUserLoggedIn = authStore.isLoggedIn

  if (!isPublicRoute && !isUserLoggedIn) {
    const fullPath = to.query.lang
      ? to.fullPath.replace(/([&?])lang=[^&]+(&|$)/, (match, p1, p2) => p1 === '?' && p2 === '&' ? '?' : p1) // Remove lang query param from fullPath.
      : to.fullPath

    return next({
      name: 'login',
      query: { redirect: fullPath, lang: to.query.lang }, // Store the full path to redirect the user to after login.
    })
  }

  // Do not allow user to visit login page or register page if they are logged in.
  if (isUserLoggedIn && unauthenticatedOnly) {
    return next({ name: 'home' })
  }

  loadLayout(to)

  next()
})

export default router
